import {
  EvictionsByYearPerMonth,
  MONTH_ORDER,
  MonthAbbrev,
  ReportFilters,
} from '../../../types/types';

export interface MonthlyPoint {
  month: MonthAbbrev;
  households: number;
  individuals: number;
  /** True once we're past the cutoff month — lets the chart grey out the tail. */
  isFuture: boolean;
}

export interface EvictionMetrics {
  /** Sum of households/individuals from Jan through the cutoff month. */
  ytdHouseholds: number;
  ytdIndividuals: number;
  /** Only populated when filters.mode is 'month' or 'both'. */
  mtdHouseholds: number | null;
  mtdIndividuals: number | null;
  /** Percent change vs. the same YTD cutoff last year. Null if no prior-year data. */
  householdsYoyPct: number | null;
  individualsYoyPct: number | null;
  /** Jan-Dec series for the bar/line charts, zero-filled for months with no data. */
  monthlySeries: MonthlyPoint[];
}

function cutoffIndex(filters: ReportFilters): number {
  // 'year' mode alone still needs a cutoff for "up to now" — fall back to
  // December so a full year with no month selected sums everything present.
  if (filters.month) return MONTH_ORDER.indexOf(filters.month);
  return MONTH_ORDER.length - 1;
}

function sumThroughCutoff(
  yearData: EvictionsByYearPerMonth[string] | undefined,
  cutoff: number,
  field: 'total_evictions_households' | 'total_evictions_individuals',
): number {
  if (!yearData) return 0;
  return MONTH_ORDER.slice(0, cutoff + 1).reduce((total, month) => {
    return total + (yearData[month]?.[field] ?? 0);
  }, 0);
}

function pctChange(current: number, prior: number): number | null {
  if (prior === 0) return null; // avoid divide-by-zero / misleading infinite %
  return ((current - prior) / prior) * 100;
}

/**
 * Computes every number section 01 (eviction numbers) needs, given the raw
 * API payload and the filters selected in the report-generation modal.
 */
export function computeEvictionMetrics(
  data: EvictionsByYearPerMonth,
  filters: ReportFilters,
): EvictionMetrics {
  const cutoff = cutoffIndex(filters);
  const yearData = data[String(filters.year)];
  const priorYearData = data[String(filters.year - 1)];

  const ytdHouseholds = sumThroughCutoff(yearData, cutoff, 'total_evictions_households');
  const ytdIndividuals = sumThroughCutoff(yearData, cutoff, 'total_evictions_individuals');

  const priorYtdHouseholds = sumThroughCutoff(priorYearData, cutoff, 'total_evictions_households');
  const priorYtdIndividuals = sumThroughCutoff(priorYearData, cutoff, 'total_evictions_individuals');

  const includesMonth = filters.mode === 'month' || filters.mode === 'both';
  const mtdHouseholds = includesMonth && filters.month
    ? yearData?.[filters.month]?.total_evictions_households ?? 0
    : null;
  const mtdIndividuals = includesMonth && filters.month
    ? yearData?.[filters.month]?.total_evictions_individuals ?? 0
    : null;

  const monthlySeries: MonthlyPoint[] = MONTH_ORDER.map((month, i) => ({
    month,
    households: yearData?.[month]?.total_evictions_households ?? 0,
    individuals: yearData?.[month]?.total_evictions_individuals ?? 0,
    isFuture: i > cutoff,
  }));

  return {
    ytdHouseholds,
    ytdIndividuals,
    mtdHouseholds,
    mtdIndividuals,
    householdsYoyPct: pctChange(ytdHouseholds, priorYtdHouseholds),
    individualsYoyPct: pctChange(ytdIndividuals, priorYtdIndividuals),
    monthlySeries,
  };
}